import React, { useState } from 'react';

import { useChatContext } from '~/components/AiChat/hooks/useChat';
import { useClaim } from '~/components/ClaimContainer';
import Menu from '~/components/core/Atomic/Menu';
import MenuItem from '~/components/core/Atomic/MenuItem';
import { EditDocumentDialog, isDocumentAPdf } from '~/components/Documents/DocumentCard';
import ManipulatePdfContainer from '~/components/Documents/DocumentMoreActions/ManipulatePdf/ManipulatePdfContainer';
import { useCms } from '~/components/hooks/useCms';
import HoverActionField from '~/components/HoverActionField';
import { ThreeDotsIcon } from '~/components/icons';
import { AddInternalCommunicationDialog } from '~/components/InternalCommunication';
import InvoiceAnalysisContainer from '~/components/invoices/InvoiceAnalysisContainer';
import type { DocumentMetaData } from '~/server_shared/swagger/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';
import { isClaimWriteDisabled } from '~/Utils/ClaimUtils';

interface DocumentMoreActionsContainerProps {
  document: DocumentMetaData;
  showAddToChat?: boolean;
}

const DocumentMoreActionsContainer: React.FC<DocumentMoreActionsContainerProps> = ({ document, showAddToChat }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isAddInternalCommunicationDialogOpen, setIsAddInternalCommunicationDialogOpen] = useState<boolean>(false);
  const [isEditDocumentDialogOpen, setIsEditDocumentDialogOpen] = useState<boolean>(false);
  const [isManipulatePdfDialogOpen, setIsManipulatePdfDialogOpen] = useState<boolean>(false);
  const [isInvoiceAnalysisOpen, setIsInvoiceAnalysisOpen] = useState<boolean>(false);

  const { claim, onAsyncClaimUpdate } = useClaim({ claimRequired: true });
  const { user, userOrganization } = useCms();
  const { setIsAiChatOpen, setChatDocumentIds, chatDocumentIds = [] } = useChatContext();

  const disabled = document.is_uploading;

  const isManipulatePdfDisabled =
    isClaimWriteDisabled(claim, user, { allowOnClosedClaim: true }) || !isDocumentAPdf(document);

  const isInvoiceAnalysisEnabled =
    isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.AI_SERVICE_ACTIONS_INVOICE_ANALYSIS_DISPLAY) ||
    isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.AI_SERVICE_ACTIONS_DISPLAY_NO_INVOICE_ANALYSIS);

  return (
    <>
      {disabled ? null : (
        <>
          <HoverActionField
            icon={ThreeDotsIcon}
            onAction={(event: React.MouseEvent<HTMLElement>) => {
              event.stopPropagation();
              setAnchorEl(event.currentTarget);
            }}
            permanent
            ignorePermissions
            actionAriaLabel="Document actions"
          />
          <Menu
            PaperProps={{
              style: {
                width: 200,
              },
            }}
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem
              onClick={() => {
                setIsAddInternalCommunicationDialogOpen(true);
                setAnchorEl(null);
              }}
            >
              Internal Communication
            </MenuItem>

            <MenuItem
              onClick={() => {
                setIsEditDocumentDialogOpen(true);
                setAnchorEl(null);
              }}
            >
              Edit Document
            </MenuItem>

            <MenuItem
              disabled={!document.has_original_file}
              component="a"
              href={`/api/v1/claims/${document.claim_id}/documents/${document.id}/original`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setAnchorEl(null)}
            >
              Show Original File
            </MenuItem>

            {isInvoiceAnalysisEnabled ? (
              <MenuItem
                onClick={() => {
                  setIsInvoiceAnalysisOpen(true);
                  setAnchorEl(null);
                }}
              >
                {document.invoice_id ? 'View Analyzed Invoice' : 'Analyze as Invoice'}
              </MenuItem>
            ) : null}

            {isManipulatePdfDisabled ? null : (
              <MenuItem
                onClick={() => {
                  setIsManipulatePdfDialogOpen(true);
                  setAnchorEl(null);
                }}
              >
                Manipulate PDF
              </MenuItem>
            )}
            {showAddToChat ? (
              <MenuItem
                onClick={() => {
                  setChatDocumentIds?.([...chatDocumentIds, document.id]);
                  setIsAiChatOpen(() => true);
                  setAnchorEl(null);
                }}
                disabled={!document.is_processed_by_ai}
              >
                Add to Chat
              </MenuItem>
            ) : null}
          </Menu>

          <AddInternalCommunicationDialog
            open={isAddInternalCommunicationDialogOpen}
            claim={claim}
            icObject={{ type: 'document', document }}
            onCancel={() => setIsAddInternalCommunicationDialogOpen(false)}
            onSubmit={() => setIsAddInternalCommunicationDialogOpen(false)}
          />

          <EditDocumentDialog
            open={isEditDocumentDialogOpen}
            claim={claim}
            onCancel={() => setIsEditDocumentDialogOpen(false)}
            document={document}
            onUpdateDocument={async () => {
              await onAsyncClaimUpdate();
              setIsEditDocumentDialogOpen(false);
            }}
          />

          <ManipulatePdfContainer
            isOpen={isManipulatePdfDialogOpen}
            document={document}
            onClose={() => setIsManipulatePdfDialogOpen(false)}
          />

          <InvoiceAnalysisContainer
            open={isInvoiceAnalysisOpen}
            onClose={() => setIsInvoiceAnalysisOpen(false)}
            documentId={document.id}
          />
        </>
      )}
    </>
  );
};

export default DocumentMoreActionsContainer;
